import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";

import config from "../../config";
import {
  AmountField,
  ConfirmPaymentDialog,
  DateField,
  Field,
  FormSection,
  MethodButtons,
  ReceiptActionButtons,
  ReceivePaymentButton,
  RecentPaymentsCard,
  ReferenceField,
  RowReceiptButtons,
  Stat,
  TableCell,
} from "../payments/shared/PaymentShared";
import {
  METHODS_NEEDING_REFERENCE,
  amountError,
  amountInputValue,
  authConfig,
  displayValue,
  formatLabel,
  newTransactionId,
  printReceiptHtml,
  publicAssetUrl,
  receiptDocument,
  receiptRow,
  receiptSection,
  receiptTotalRow,
  saveBlob,
  todayAsInputValue,
} from "../payments/shared/paymentUtils";
import {
  FAMILY_PAYMENT_KIND_LABELS,
  fetchFamilyDetails,
  formatLkr,
} from "./hpcMembershipPricing";
import { HPC_COLORS } from "./hpcTheme";
import { formatStatusLabel, getStatusChipSx } from "./hpcAdminStatusStyles";
import { isFamilyPackage } from "./hpcFormConfig";
import { FamilyBreakdownTable, familyCategoryLabel } from "./FamilyPriceTable";

const receiptPrintHtml = (receipt) => {
  const row = receiptRow;
  const amount = receipt.payment?.amount == null
    ? "—"
    : `LKR ${Number(receipt.payment.amount).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`;
  const body = [
    receiptSection("Receipt Information", `${row("Receipt Number", receipt.receipt_number)}${row("Payment Date", receipt.payment_date)}${row("Payment Time", receipt.payment_time)}${row("Received By", formatLabel(receipt.received_by))}`),
    receiptSection("Customer Information", `${row("Member ID", receipt.member?.membership_id)}${row("Customer Name", receipt.member?.full_name)}${row("Mobile Number", receipt.member?.mobile_number)}`),
    receiptSection("Payment Information", `${row("Package / Service", formatLabel(receipt.payment?.package_service))}${row("Description", receipt.payment?.description)}${row("Payment Method", formatLabel(receipt.payment?.payment_method))}${row("Transaction ID", receipt.payment?.transaction_id)}${row("Payment Reference", receipt.payment?.payment_reference)}${receipt.promotion ? row("Original Value", formatLkr(receipt.promotion.original_value)) + row("Promotion", `${receipt.promotion.code} −${formatLkr(receipt.promotion.discount_amount)}`) + row("Promotion Price", formatLkr(receipt.promotion.final_amount)) : ""}${receiptTotalRow("Amount", amount)}${row("Previous Outstanding", receipt.payment?.previous_outstanding_amount)}${row("Balance After Payment", receipt.payment?.balance_after_payment)}`),
    receiptSection("Membership Information", `${row("Package", formatLabel(receipt.membership?.package))}${row("Activation Date", receipt.membership?.activation_date)}${row("Expiry Date", receipt.membership?.expiry_date)}${row("Membership Status", formatLabel(receipt.membership?.status))}`),
    receipt.family ? receiptSection("Family Members", `${row("Family Group", receipt.family.group_id)}${(receipt.family.members || []).map((m) => row(`#${m.position ?? "—"} ${m.full_name || ""}`, `${familyCategoryLabel(m.price_category)} - ${formatLkr(m.net_fee)}`)).join("")}${receipt.family.expected_total != null ? row("Expected Family Total", formatLkr(receipt.family.expected_total)) : ""}`) : "",
  ].join("\n");
  return receiptDocument({
    title: receipt.receipt_number,
    logoSrc: publicAssetUrl("/images/teba-hpu-logo.png"),
    logoAlt: "TEBA HPU logo",
    brandHtml: "TEBA HPU",
    subtitleHtml: "Health &amp; Performance Unit",
    body,
  });
};

const PURPOSE_MEMBERSHIP = "membership";
const PURPOSE_MEMBER_JOINING = "family_member_joining";
const PURPOSE_LABELS = {
  [PURPOSE_MEMBERSHIP]: "Family membership (activation / renewal)",
  [PURPOSE_MEMBER_JOINING]: "New family member joining",
};

// "Payment for" choices. The member's own package is the default; staff can still change it.
const FOR_PACKAGE = "package";
const FOR_JOINING = "joining";
const FOR_OTHER = "other";

const emptyPaymentForm = () => ({
  amount: "",
  paymentDate: todayAsInputValue(),
  paymentMethod: "cash",
  paymentReference: "",
  paymentFor: FOR_OTHER,
  paymentCategory: "",
  description: "",
  remarks: "",
});

const groupLabel = (membershipType) => {
  if (isFamilyPackage(membershipType)) return "Family group";
  if (String(membershipType || "").startsWith("couple_")) return "Couple group";
  return "Group";
};

const HpuPaymentPanel = ({ registration, onPaymentCompleted, showMemberSummary = true }) => {
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(!showMemberSummary);
  const [detailsOpen, setDetailsOpen] = useState(!showMemberSummary);
  const [breakdownOpen, setBreakdownOpen] = useState(!showMemberSummary);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetailLoading, setPaymentDetailLoading] = useState(false);
  const [paymentDetailError, setPaymentDetailError] = useState("");
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentSubmitError, setPaymentSubmitError] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState("");
  const [receiptOpen, setReceiptOpen] = useState(false);
  const paymentTransactionIdRef = useRef(null);
  // True once staff edit the form; defaults are only filled in while it is untouched.
  const formTouchedRef = useRef(false);
  const [familyDetails, setFamilyDetails] = useState(null);
  const [familyLoaded, setFamilyLoaded] = useState(false);
  const [familyError, setFamilyError] = useState("");
  const isFamily = isFamilyPackage(registration?.membership_type);
  const [groupMain, setGroupMain] = useState(null);

  // Couple and Family: only the main member pays; other members are paid through them.
  useEffect(() => {
    const groupId = registration?.group_id;
    if (!groupId) {
      setGroupMain(null);
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`${config.apiBaseUrl}/hpc/gym-registration/group/${groupId}`)
      .then((response) => {
        if (cancelled) return;
        const mainId = response.data.primary_membership_id;
        const main = (response.data.members || []).find((m) => m.membership_id === mainId);
        setGroupMain(mainId ? { membershipId: mainId, fullName: main?.full_name } : null);
      })
      .catch(() => {
        if (!cancelled) setGroupMain(null);
      });
    return () => {
      cancelled = true;
    };
  }, [registration?.group_id]);

  const paidThroughMainMember = Boolean(
    groupMain && registration && groupMain.membershipId !== registration.membership_id,
  );

  // Family groups only: the expected amount is the server's own calculation.
  const loadFamily = useCallback(async () => {
    if (!isFamily || !registration?.membership_id) {
      setFamilyDetails(null);
      setFamilyLoaded(true);
      return;
    }
    setFamilyError("");
    try {
      setFamilyDetails(await fetchFamilyDetails(registration.membership_id));
    } catch (error) {
      setFamilyDetails(null);
      setFamilyError(error.response?.data?.error || "Could not load the family payment details.");
    } finally {
      setFamilyLoaded(true);
    }
  }, [isFamily, registration?.membership_id]);

  useEffect(() => {
    setFamilyLoaded(false);
    loadFamily();
  }, [loadFamily]);

  const loadHistory = useCallback(async () => {
    if (!registration?.id) return;
    setHistoryError("");
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/payment-receive/hpu/registrations/${registration.id}/payments`,
        authConfig(),
      );
      setHistory(response.data.payments || []);
    } catch (error) {
      setHistoryError(error.response?.data?.error || "Could not load HPU payment information.");
      setHistory([]);
    } finally {
      setHistoryLoaded(true);
    }
  }, [registration?.id]);

  useEffect(() => {
    paymentTransactionIdRef.current = null;
    formTouchedRef.current = false;
    setHistory([]);
    setHistoryLoaded(false);
    setHistoryError("");
    setSelectedPayment(null);
    setPaymentSuccess(null);
    setPaymentSubmitError("");
    setReceipt(null);
    setReceiptError("");
    setReceiptOpen(false);
    setPaymentErrors({});
    setPaymentForm(emptyPaymentForm());
    loadHistory();
  }, [loadHistory, registration?.id]);

  const latestPayment = history[0];
  const packageLabel = registration?.membership_type ? formatLabel(registration.membership_type) : "";
  const pendingJoining = familyDetails?.pending_joining?.members?.length > 0
    ? familyDetails.pending_joining
    : null;

  // Expected / outstanding amount, calculated as before:
  // Family: the server's family quote. Others: the same rule the server uses for
  // "previous outstanding" (latest balance, or the saved package total less what was paid).
  let expectedAmount = null;
  if (registration && !paidThroughMainMember) {
    if (isFamily) {
      if (familyDetails?.expected_payment?.amount != null) {
        expectedAmount = Number(familyDetails.expected_payment.outstanding || 0);
      }
    } else if (latestPayment && latestPayment.balance_after_payment != null) {
      expectedAmount = Number(latestPayment.balance_after_payment);
    } else if (historyLoaded) {
      const packageTotal = Number(registration.membership_fee || 0) + Number(registration.registration_fee || 0);
      expectedAmount = Math.max(packageTotal - Number(registration.total_amount_paid || 0), 0);
    }
  }

  // Pre-fill "Payment for" with the member's own package and the amount with the expected amount.
  useEffect(() => {
    if (formTouchedRef.current || !historyLoaded || !familyLoaded || paidThroughMainMember) return;
    setPaymentForm((current) => ({
      ...current,
      paymentFor: packageLabel ? FOR_PACKAGE : FOR_OTHER,
      amount: amountInputValue(expectedAmount),
    }));
  }, [historyLoaded, familyLoaded, paidThroughMainMember, packageLabel, expectedAmount]);

  const resetPaymentForm = () => {
    paymentTransactionIdRef.current = null;
    formTouchedRef.current = false;
    setPaymentForm(emptyPaymentForm());
    setPaymentErrors({});
  };

  const updatePaymentForm = (field) => (event) => {
    formTouchedRef.current = true;
    setPaymentForm((current) => ({ ...current, [field]: event.target.value }));
    setPaymentErrors((current) => ({ ...current, [field]: "" }));
    setPaymentSubmitError("");
  };

  const choosePaymentFor = (paymentFor) => {
    formTouchedRef.current = true;
    setPaymentForm((current) => {
      if (paymentFor === FOR_JOINING && pendingJoining) {
        return { ...current, paymentFor, amount: String(pendingJoining.amount) };
      }
      if (paymentFor === FOR_PACKAGE) {
        return { ...current, paymentFor, amount: amountInputValue(expectedAmount) || current.amount };
      }
      return { ...current, paymentFor };
    });
    setPaymentErrors((current) => ({ ...current, amount: "" }));
    setPaymentSubmitError("");
  };

  const chooseMethod = (_event, method) => {
    if (!method) return;
    formTouchedRef.current = true;
    setPaymentForm((current) => ({ ...current, paymentMethod: method }));
    setPaymentErrors((current) => ({ ...current, paymentMethod: "", paymentReference: "" }));
    setPaymentSubmitError("");
  };

  const needsReference = METHODS_NEEDING_REFERENCE.includes(paymentForm.paymentMethod);
  const purpose = paymentForm.paymentFor === FOR_JOINING ? PURPOSE_MEMBER_JOINING : PURPOSE_MEMBERSHIP;
  const paymentCategoryValue = paymentForm.paymentFor === FOR_PACKAGE
    ? packageLabel
    : paymentForm.paymentCategory.trim();
  const packageOptionLabel = isFamily
    ? `${packageLabel} – ${PURPOSE_LABELS[PURPOSE_MEMBERSHIP].toLowerCase()}`
    : `${packageLabel} – membership (activation / renewal)`;

  const validatePaymentForm = () => {
    const errors = {};
    const amountProblem = amountError(paymentForm.amount);
    if (amountProblem) errors.amount = amountProblem;
    if (!paymentForm.paymentDate) errors.paymentDate = "Payment date is required.";
    if (!paymentForm.paymentMethod) errors.paymentMethod = "Payment method is required.";
    if (needsReference && !paymentForm.paymentReference.trim()) {
      errors.paymentReference = "Payment reference is required for this method.";
    }
    setPaymentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openPaymentConfirmation = () => {
    setPaymentSubmitError("");
    if (!registration || !validatePaymentForm()) return;
    setConfirmOpen(true);
  };

  const fetchReceipt = async (paymentId) => {
    if (!paymentId) return null;
    setReceiptLoading(true);
    setReceiptError("");
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/payment-receive/hpu/payments/${paymentId}/receipt`,
        authConfig(),
      );
      const nextReceipt = response.data.receipt || null;
      setReceipt(nextReceipt);
      return nextReceipt;
    } catch (error) {
      setReceiptError(error.response?.data?.error || "Could not load the receipt.");
      return null;
    } finally {
      setReceiptLoading(false);
    }
  };

  const viewReceipt = async (paymentId = paymentSuccess?.id) => {
    const nextReceipt = receipt && receipt.payment?.transaction_id === paymentSuccess?.transaction_id
      ? receipt
      : await fetchReceipt(paymentId);
    if (nextReceipt) setReceiptOpen(true);
  };

  const printReceipt = async (paymentId = paymentSuccess?.id) => {
    const nextReceipt = receipt && receipt.payment?.transaction_id === paymentSuccess?.transaction_id
      ? receipt
      : await fetchReceipt(paymentId);
    if (!nextReceipt) return;
    if (!printReceiptHtml(receiptPrintHtml(nextReceipt))) {
      setReceiptError("The receipt window was blocked. Please allow pop-ups and try again.");
    }
  };

  const downloadReceiptPdf = async (paymentId = paymentSuccess?.id, receiptNumber = paymentSuccess?.receipt_number) => {
    if (!paymentId) return;
    setReceiptLoading(true);
    setReceiptError("");
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/payment-receive/hpu/payments/${paymentId}/receipt.pdf`,
        { ...authConfig(), responseType: "blob" },
      );
      saveBlob(response.data, `${receiptNumber || "TEBAHPU-receipt"}.pdf`);
    } catch (error) {
      setReceiptError(error.response?.data?.error || "Could not download the receipt PDF.");
    } finally {
      setReceiptLoading(false);
    }
  };

  const submitPayment = async () => {
    if (submittingPayment || !registration || !validatePaymentForm()) return;
    if (!paymentTransactionIdRef.current) paymentTransactionIdRef.current = newTransactionId();
    setSubmittingPayment(true);
    setPaymentSubmitError("");
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/payment-receive/hpu/payments`,
        {
          hpc_registration_id: registration.id,
          amount: paymentForm.amount,
          payment_method: paymentForm.paymentMethod,
          // The reference field is only shown for bank transfer and card.
          payment_reference: needsReference ? paymentForm.paymentReference.trim() || null : null,
          transaction_id: paymentTransactionIdRef.current,
          payment_datetime: `${paymentForm.paymentDate}T00:00:00`,
          payment_category: paymentCategoryValue || null,
          description: paymentForm.description.trim() || null,
          remarks: paymentForm.remarks.trim() || null,
          ...(purpose === PURPOSE_MEMBER_JOINING
            ? { payment_purpose: PURPOSE_MEMBER_JOINING }
            : {}),
        },
        authConfig(),
      );
      setConfirmOpen(false);
      setPaymentSuccess({
        ...(response.data.payment || {}),
        registration: response.data.registration || null,
        sms: response.data.sms || null,
        receipt_sms: response.data.receipt_sms || null,
        activation_sms: response.data.activation_sms || null,
      });
      resetPaymentForm();
      await loadHistory();
      await loadFamily();
      if (onPaymentCompleted) await onPaymentCompleted(response.data.registration || null);
    } catch (error) {
      setPaymentSubmitError(error.response?.data?.error || "Could not record the HPU payment. Please try again.");
    } finally {
      setSubmittingPayment(false);
    }
  };

  const openPaymentDetails = async (payment) => {
    setSelectedPayment(payment);
    setPaymentDetailError("");
    setPaymentDetailLoading(true);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/payment-receive/hpu/payments/${payment.id}`,
        authConfig(),
      );
      setSelectedPayment(response.data.payment || payment);
    } catch (error) {
      setPaymentDetailError(error.response?.data?.error || "Could not load payment details.");
    } finally {
      setPaymentDetailLoading(false);
    }
  };

  if (!registration) return null;

  const accentBorder = `1px solid ${alpha(HPC_COLORS.hpuOrange, 0.45)}`;
  const expectedLabel = isFamily ? "Outstanding now" : "Outstanding";

  return (
    <>
      <Card sx={{ mb: 2, border: accentBorder }}>
        <CardContent>
          {showMemberSummary ? (
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="h4" fontWeight={700}>{registration.full_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {registration.membership_id}
                  {registration.mobile_number ? ` · ${registration.mobile_number}` : ""}
                </Typography>
              </Box>
              <Chip label={formatStatusLabel(registration.status)} sx={getStatusChipSx(registration.status)} />
            </Box>
          ) : (
            <>
              <Typography variant="h5" fontWeight={700}>Payment summary</Typography>
              <Typography variant="body2" color="text.secondary">
                Current recorded financial state before a new payment is entered.
              </Typography>
            </>
          )}
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Stat label="Package" value={packageLabel} />
            <Stat label="Expiry date" value={registration.expiry_date} />
            {paidThroughMainMember ? (
              <Stat label="Payment" value="Paid through main member" />
            ) : (
              <Stat
                label={expectedLabel}
                value={expectedAmount == null ? (historyLoaded ? null : "Loading…") : formatLkr(expectedAmount)}
                highlight
              />
            )}
            {registration.group_id && (
              <Stat
                label={groupLabel(registration.membership_type)}
                value={groupMain
                  ? `${registration.group_id} · Main: ${groupMain.fullName || groupMain.membershipId}`
                  : registration.group_id}
              />
            )}
          </Grid>

          {paidThroughMainMember && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Paid through main member: {groupMain.fullName || "Main member"} ({groupMain.membershipId}).{" "}
              <Button
                component={RouterLink}
                to={`/hpc/members/${groupMain.membershipId}`}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              >
                Open main member
              </Button>
            </Alert>
          )}
          {familyError && <Alert severity="error" sx={{ mt: 2 }}>{familyError}</Alert>}
          {!paidThroughMainMember && pendingJoining && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={700}>
                New family members waiting for their joining payment ({formatLkr(pendingJoining.amount)})
              </Typography>
              {pendingJoining.members.map((member) => (
                <Typography key={member.membership_id} variant="body2">
                  {member.full_name || member.membership_id} ({member.membership_id}) · position {member.position ?? "—"} · {formatLkr(member.joining_due)}
                </Typography>
              ))}
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                A joining payment activates them on the family's current dates. It does not change the family's expiry.
              </Typography>
              <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => choosePaymentFor(FOR_JOINING)}>
                Record joining payment
              </Button>
            </Alert>
          )}

          <Button
            size="small"
            onClick={() => setDetailsOpen((open) => !open)}
            endIcon={<ExpandMoreIcon sx={{ transform: detailsOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
            sx={{ mt: 1.5, color: "text.secondary" }}
          >
            {detailsOpen ? "Hide payment details" : "Show payment details"}
          </Button>
          <Collapse in={detailsOpen} unmountOnExit>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Field label="Package membership fee" value={formatLkr(registration.membership_fee)} />
              <Field label="Registration fee" value={formatLkr(registration.registration_fee)} />
              <Field label="Activation date" value={registration.activation_date} />
              <Field label="Previous outstanding" value={latestPayment ? formatLkr(latestPayment.previous_outstanding_amount) : "Not recorded"} />
              <Field label="Balance after latest payment" value={latestPayment ? formatLkr(latestPayment.balance_after_payment) : "Not recorded"} />
              <Field label="Last payment date" value={latestPayment?.payment_datetime} />
              <Field label="Last payment amount" value={latestPayment ? formatLkr(latestPayment.amount) : null} />
              {registration.promotion_code && (() => {
                const normal = Number(registration.original_membership_fee || 0) + Number(registration.original_admission_fee || 0);
                const discount = Number(registration.promo_discount_amount || 0);
                // Original value = saved fees (already after the promotion) + saved discount.
                const original = Number(registration.membership_fee || 0) + Number(registration.registration_fee || 0) + discount;
                return (
                  <>
                    <Field label="Original value" value={formatLkr(original)} />
                    <Field label="Promotion" value={`${registration.promotion_code} −${formatLkr(discount)}`} />
                    <Field label="Promotion price" value={formatLkr(Number(original) - discount)} />
                    <Field label="Normal price" value={formatLkr(normal)} />
                  </>
                );
              })()}
            </Grid>
            {registration.promotion_code && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Fees above already include the promotion.
              </Typography>
            )}
          </Collapse>

          {!paidThroughMainMember && familyDetails && familyDetails.expected_payment?.amount != null && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" fontWeight={700}>Family payment</Typography>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Field label="Family group" value={familyDetails.group_id} />
                <Field label="Payment type" value={FAMILY_PAYMENT_KIND_LABELS[familyDetails.expected_payment.kind]} />
                <Field label="Expected family total" value={formatLkr(familyDetails.expected_payment.amount)} />
                <Field label="Outstanding now" value={formatLkr(familyDetails.expected_payment.outstanding)} />
              </Grid>
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                {familyDetails.expected_payment.outstanding > 0 && (
                  <Button size="small" variant="outlined" onClick={() => choosePaymentFor(FOR_PACKAGE)}>
                    Use outstanding amount
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={() => setBreakdownOpen((open) => !open)}
                  endIcon={<ExpandMoreIcon sx={{ transform: breakdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
                  sx={{ color: "text.secondary" }}
                >
                  {breakdownOpen ? "Hide member breakdown" : "Show member breakdown"}
                </Button>
              </Box>
              <Collapse in={breakdownOpen} unmountOnExit>
                <FamilyBreakdownTable
                  rows={familyDetails.expected_payment.quote?.members || familyDetails.members}
                  showAdmission={!familyDetails.expected_payment.quote}
                  totals={[["Expected family total", familyDetails.expected_payment.amount]]}
                />
              </Collapse>
            </Box>
          )}
        </CardContent>
      </Card>

      {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}
      {paymentSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Payment recorded successfully. Transaction {displayValue(paymentSuccess.transaction_id)} was saved for {registration.membership_id}.
          {paymentSuccess.registration && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Receipt {displayValue(paymentSuccess.receipt_number)} · Membership {formatStatusLabel(paymentSuccess.registration.status)}
              {paymentSuccess.registration.activation_date ? ` · Active from ${paymentSuccess.registration.activation_date}` : ""}
              {paymentSuccess.registration.expiry_date ? ` · Expires ${paymentSuccess.registration.expiry_date}` : ""}
            </Typography>
          )}
          {paymentSuccess.sms && <Typography variant="body2" sx={{ mt: 0.5 }}>{paymentSuccess.sms.sent ? "Payment confirmation SMS sent." : "Payment saved, but the confirmation SMS could not be sent."}</Typography>}
          {paymentSuccess.receipt_sms && <Typography variant="body2" sx={{ mt: 0.5 }}>{paymentSuccess.receipt_sms.sent ? "Receipt SMS sent." : "Payment saved, but the receipt SMS could not be sent."}</Typography>}
          {paymentSuccess.activation_sms && paymentSuccess.registration?.status === "active" && <Typography variant="body2" sx={{ mt: 0.5 }}>{paymentSuccess.activation_sms.sent ? "Membership activation SMS sent." : "Membership activated, but the activation SMS could not be sent."}</Typography>}
          <ReceiptActionButtons
            onView={() => viewReceipt(paymentSuccess.id)}
            onPrint={() => printReceipt(paymentSuccess.id)}
            onPdf={() => downloadReceiptPdf(paymentSuccess.id, paymentSuccess.receipt_number)}
            disabled={receiptLoading}
          />
        </Alert>
      )}
      {paymentSubmitError && <Alert severity="error" sx={{ mb: 2 }}>{paymentSubmitError}</Alert>}
      {receiptError && <Alert severity="error" sx={{ mb: 2 }}>{receiptError}</Alert>}

      {!paidThroughMainMember && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={700}>Receive payment</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Enter the payment details, then review them before saving.
            </Typography>

            <FormSection title="Payment for">
              <FormControl fullWidth>
                <InputLabel id="hpu-payment-for-label">Payment for</InputLabel>
                <Select
                  labelId="hpu-payment-for-label"
                  label="Payment for"
                  value={paymentForm.paymentFor}
                  onChange={(event) => choosePaymentFor(event.target.value)}
                >
                  {packageLabel && <MenuItem value={FOR_PACKAGE}>{packageOptionLabel}</MenuItem>}
                  {pendingJoining && <MenuItem value={FOR_JOINING}>{PURPOSE_LABELS[PURPOSE_MEMBER_JOINING]}</MenuItem>}
                  <MenuItem value={FOR_OTHER}>Other payment</MenuItem>
                </Select>
              </FormControl>
              {paymentForm.paymentFor !== FOR_PACKAGE && (
                <TextField
                  fullWidth
                  label="Payment category"
                  value={paymentForm.paymentCategory}
                  onChange={updatePaymentForm("paymentCategory")}
                  sx={{ mt: 1.5 }}
                  helperText={paymentForm.paymentFor === FOR_JOINING ? "Optional" : "Optional, e.g. personal training"}
                />
              )}
            </FormSection>

            <FormSection title="Amount">
              <AmountField
                value={paymentForm.amount}
                onChange={updatePaymentForm("amount")}
                error={paymentErrors.amount}
                helperText={
                  paymentForm.paymentFor === FOR_PACKAGE && expectedAmount > 0
                    ? `${expectedLabel}: ${formatLkr(expectedAmount)}. Positive amount, maximum 2 decimals.`
                    : "Positive amount, maximum 2 decimals."
                }
              />
            </FormSection>

            <FormSection title="Method">
              <MethodButtons value={paymentForm.paymentMethod} onChange={chooseMethod} error={paymentErrors.paymentMethod} />
            </FormSection>

            {needsReference && (
              <ReferenceField
                value={paymentForm.paymentReference}
                onChange={updatePaymentForm("paymentReference")}
                error={paymentErrors.paymentReference}
              />
            )}

            <DateField
              value={paymentForm.paymentDate}
              onChange={updatePaymentForm("paymentDate")}
              error={paymentErrors.paymentDate}
            />

            <FormSection title="Notes">
              <Grid container spacing={2}>
                <Grid item xs={12}><TextField fullWidth label="Description" value={paymentForm.description} onChange={updatePaymentForm("description")} /></Grid>
                <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Remarks" value={paymentForm.remarks} onChange={updatePaymentForm("remarks")} /></Grid>
              </Grid>
            </FormSection>

            <ReceivePaymentButton onClick={openPaymentConfirmation} disabled={submittingPayment} />
          </CardContent>
        </Card>
      )}

      <RecentPaymentsCard
        rows={history}
        loaded={historyLoaded}
        open={historyOpen}
        onToggle={() => setHistoryOpen((open) => !open)}
        headers={["Receipt", "Date", "Description", "Amount", "Method", "Received by", "Status", "Receipt actions"]}
        rowKey={(payment) => payment.id || payment.transaction_id}
        onRowClick={openPaymentDetails}
        emptyText="No HPU payment history is available for this member."
        renderCells={(payment) => <>
          <TableCell>{displayValue(payment.receipt_number)}</TableCell><TableCell>{displayValue(payment.payment_datetime)}</TableCell><TableCell>{displayValue(payment.description || payment.payment_category)}</TableCell><TableCell>{formatLkr(payment.amount)}</TableCell><TableCell>{formatLabel(payment.payment_method)}</TableCell><TableCell>{displayValue(payment.received_by)}</TableCell><TableCell>{formatLabel(payment.payment_status)}</TableCell>
          <TableCell align="right"><RowReceiptButtons onView={() => viewReceipt(payment.id)} onPrint={() => printReceipt(payment.id)} onPdf={() => downloadReceiptPdf(payment.id, payment.receipt_number)} disabled={!payment.id || receiptLoading} /></TableCell>
        </>}
      />

      <Dialog open={Boolean(selectedPayment)} onClose={() => !paymentDetailLoading && setSelectedPayment(null)} fullWidth maxWidth="sm">
        <DialogTitle>Payment details</DialogTitle><DialogContent dividers>
          {paymentDetailLoading ? <CircularProgress size={24} /> : paymentDetailError ? <Alert severity="error">{paymentDetailError}</Alert> : selectedPayment && <Grid container spacing={2}>
            <Field label="Payment ID" value={selectedPayment.id} /><Field label="Transaction ID" value={selectedPayment.transaction_id} /><Field label="Receipt number" value={selectedPayment.receipt_number} /><Field label="Amount" value={formatLkr(selectedPayment.amount)} /><Field label="Payment method" value={formatLabel(selectedPayment.payment_method)} /><Field label="Payment reference" value={selectedPayment.payment_reference} /><Field label="Category" value={formatLabel(selectedPayment.payment_category)} /><Field label="Description" value={selectedPayment.description} /><Field label="Received by" value={selectedPayment.received_by} /><Field label="Status" value={formatLabel(selectedPayment.payment_status)} />
          </Grid>}</DialogContent><DialogActions><Button onClick={() => setSelectedPayment(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={receiptOpen} onClose={() => setReceiptOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>HPU Payment Receipt</DialogTitle><DialogContent dividers>
          {receiptLoading && !receipt ? <CircularProgress size={24} /> : receipt && <Box sx={{ color: "text.primary" }}>
            <Box textAlign="center" mb={2}><Box component="img" src={`${process.env.PUBLIC_URL}/images/teba-hpu-logo.png`} alt="TEBA HPU logo" sx={{ maxWidth: 150, maxHeight: 72, objectFit: "contain" }} /><Typography variant="h5" fontWeight={700}>TEBA HPU</Typography><Typography variant="body2" color="text.secondary">Health &amp; Performance Unit</Typography></Box><Divider sx={{ mb: 2 }} />
            <Grid container spacing={1.5}><Field label="Receipt number" value={receipt.receipt_number} /><Field label="Payment date" value={receipt.payment_date} /><Field label="Payment time" value={receipt.payment_time} /><Field label="Received by" value={receipt.received_by} /><Field label="Member ID" value={receipt.member?.membership_id} /><Field label="Customer name" value={receipt.member?.full_name} /><Field label="Mobile number" value={receipt.member?.mobile_number} /><Field label="Package / service" value={formatLabel(receipt.payment?.package_service)} /><Field label="Description" value={receipt.payment?.description} /><Field label="Amount" value={formatLkr(receipt.payment?.amount)} /><Field label="Payment method" value={formatLabel(receipt.payment?.payment_method)} /><Field label="Transaction ID" value={receipt.payment?.transaction_id} /><Field label="Payment reference" value={receipt.payment?.payment_reference} /><Field label="Previous outstanding" value={formatLkr(receipt.payment?.previous_outstanding_amount)} /><Field label="Balance after payment" value={formatLkr(receipt.payment?.balance_after_payment)} /><Field label="Membership package" value={formatLabel(receipt.membership?.package)} /><Field label="Activation date" value={receipt.membership?.activation_date} /><Field label="Expiry date" value={receipt.membership?.expiry_date} /><Field label="Membership status" value={formatStatusLabel(receipt.membership?.status)} /></Grid>
            {receipt.promotion && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Original value {formatLkr(receipt.promotion.original_value)} · Promotion {receipt.promotion.code}{" "}
                −{formatLkr(receipt.promotion.discount_amount)} · Promotion price {formatLkr(receipt.promotion.final_amount)}
              </Alert>
            )}
            {receipt.family && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Family members ({receipt.family.group_id})</Typography>
                <FamilyBreakdownTable
                  rows={receipt.family.members || []}
                  totals={receipt.family.expected_total != null ? [["Expected family total", receipt.family.expected_total]] : []}
                />
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 3 }}>Thank you for your payment. This is a computer generated receipt.</Typography>
          </Box>}
        </DialogContent><DialogActions><Button onClick={() => setReceiptOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <ConfirmPaymentDialog open={confirmOpen} title="Confirm HPU payment" onClose={() => setConfirmOpen(false)} onConfirm={submitPayment} submitting={submittingPayment}>
        <Field label="Member ID" value={registration.membership_id} /><Field label="Member name" value={registration.full_name} /><Field label="Package/service" value={formatLabel(registration.membership_type)} />{purpose === PURPOSE_MEMBER_JOINING && <Field label="Payment for" value={PURPOSE_LABELS[PURPOSE_MEMBER_JOINING]} />}<Field label="Payment amount" value={formatLkr(paymentForm.amount)} /><Field label="Payment date" value={paymentForm.paymentDate} /><Field label="Payment method" value={formatLabel(paymentForm.paymentMethod)} /><Field label="Payment reference" value={needsReference ? paymentForm.paymentReference : ""} /><Field label="Category" value={paymentCategoryValue} /><Field label="Description" value={paymentForm.description} /><Field label="Remarks" value={paymentForm.remarks} />
      </ConfirmPaymentDialog>
    </>
  );
};

export default HpuPaymentPanel;
